import type { VNode } from "preact";
import { useEffect, useState } from "preact/hooks";
import { getCustomSource, setCustomSource } from "../settings";
import { DonationBanner } from "./DonationBanner";

type SourceInfo = {
  url: string | null;
  host_count: number;
  last_update: string | null;
};

type FieldDoc = {
  name: string;
  type: string;
  desc: string;
  example?: string;
};

/** Schema of the top level of the uptime-JSON feed. */
const TOP_FIELDS: FieldDoc[] = [
  { name: "hosts", type: "Host[]", desc: "Array of instance objects — one per Nitter instance. This is the only required field.", example: "[ … ]" },
  { name: "last_update", type: "string | null", desc: "ISO-8601 timestamp of when the feed was last regenerated. Surfaced in the dashboard after each poll.", example: "2026-10-13T20:09:52.106Z" },
  { name: "latest_commit", type: "string?", desc: "Git commit hash of the newest known Nitter version, used to compare against each instance's reported version.", example: "145ca9eb68d195a74598ae39a4d6da002b252c38" },
];

/** Schema of each item inside `hosts[]`. */
const HOST_FIELDS: FieldDoc[] = [
  { name: "domain", type: "string", desc: "Hostname of the Nitter instance. Used as the unique key in the tracker's KV store — must be unique.", example: "nitter.tiekoetter.com" },
  { name: "url", type: "string", desc: "Origin URL the instance is reachable at (https). All proxying and embedding is built from this.", example: "https://nitter.tiekoetter.com" },
  { name: "healthy", type: "boolean", desc: "Whether the feed considers the instance up right now. Drives the Down badge, the healthy filter, and whether to pick the instance.", example: "true" },
  { name: "ping_avg", type: "number | null", desc: "Mean response time in ms over the feed's recent checks. Feeds the latency part of the observation score (5000 ms maps to the slowest bucket).", example: "29" },
  { name: "ping_min / ping_max", type: "number | null", desc: "Fastest / slowest recent response in ms. Informational.", example: "18 / 111" },
  { name: "recent_pings", type: "(number | null)[]", desc: "Raw list of the last ~12 ping samples; null entries are failed probes.", example: "[111, 20, 19, null]" },
  { name: "recent_checks", type: "[string, boolean][]", desc: "Timestamped up/down history used by the feed's own status page. Not used by the tracker's scoring.", example: '[[\"2026.10.13 14:49\", true]]' },
  { name: "points", type: "number | null", desc: "The feed's own ranking score. Informational only — the tracker computes its own.", example: "66" },
  { name: "rss", type: "boolean | null", desc: "Whether the instance still serves RSS feeds. Adds a small bonus (+5) to the observation score and shows the RSS chip.", example: "true" },
  { name: "version", type: "string | null", desc: "Nitter version string the instance reports.", example: "2026.10.10-145ca9e" },
  { name: "version_state", type: "string | null", desc: "Version comparison result, e.g. Current, Outdated, UnknownCommit, Missing. Shown as a warning chip when not “running”.", example: "Current" },
  { name: "version_url", type: "string | null", desc: "Link to the commit the instance runs.", example: "https://github.com/zedeus/nitter/commit/145ca9e…" },
  { name: "is_upstream", type: "boolean | null", desc: "True when the instance runs the upstream zedeus/nitter codebase rather than a fork.", example: "true" },
  { name: "is_latest_version", type: "boolean | null", desc: "Whether the instance matches latest_commit.", example: "true" },
  { name: "is_bad_host", type: "boolean | null", desc: "Feed's flag for misbehaving instances.", example: "false" },
  { name: "country", type: "string | null", desc: "Country flag(s) where the instance is hosted.", example: "DE" },
  { name: "connectivity", type: "string | null", desc: "Network connectivity of the checker: All, IPv4, IPv6…", example: "All" },
  { name: "last_healthy", type: "string | null", desc: "ISO-8601 timestamp of the last successful check. Powers the “Last ok” column.", example: "2026-10-13T20:09:40Z" },
  { name: "healthy_percentage_overall", type: "number | null", desc: "Long-run uptime percentage. Seeds the EWMA score the first time an instance is seen.", example: "95" },
];

/** What the tracker derives and stores in KV (GET /api/hosts). */
const DERIVED_FIELDS: FieldDoc[] = [
  { name: "score", type: "number", desc: "EWMA (α = 0.2) of per-poll observations. Each observation maps health+latency+RSS onto 0–100.", example: "96.1" },
  { name: "bias", type: "number", desc: "Manual preference, −50…+50 — shown as “Priority” in the dashboard UI. Boost/Penalize moves it by ±10; it decays ×0.98 every poll so the list self-heals.", example: "10" },
  { name: "effective", type: "number", desc: "clamp(score + bias, 0, 100) — the number actually used for ranking and weighted-random picking.", example: "96.1" },
  { name: "samples", type: "number", desc: "How many polls have been observed for this instance.", example: "5" },
  { name: "consecutive_failures", type: "number", desc: "Current streak of failed polls.", example: "0" },
  { name: "blocks_iframe", type: "boolean", desc: "Result of the poller's HEAD probe for X-Frame-Options / CSP frame-ancestors. Tells the instance picker to use the proxy.", example: "false" },
  { name: "updated", type: "string", desc: "Last time this entry changed.", example: "2026-10-13T20:10:02Z" },
];

function FieldTable({ title, fields }: { title: string; fields: FieldDoc[] }): VNode {
  return (
    <div class="ds-table-wrap">
      <h3 class="ds-table-title">{title}</h3>
      <table class="ds-table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Type</th>
            <th>Description</th>
            <th>Example</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f.name}>
              <td class="ds-table__name">{f.name}</td>
              <td class="ds-table__type">{f.type}</td>
              <td>{f.desc}</td>
              <td class="ds-table__example">{f.example}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DataSourcePage(): VNode {
  const [info, setInfo] = useState<SourceInfo | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [srcDraft, setSrcDraft] = useState(getCustomSource());
  const [saved, setSaved] = useState(false);

  const load = () => {
    fetch("/api/source")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d: unknown) => {
        setInfo(d as SourceInfo);
        setErr(null);
      })
      .catch((e) => setErr((e as Error).message));
  };

  useEffect(load, []);

  const effectiveUrl = srcDraft.trim() || info?.url || "(unknown)";
  const usingCustom = srcDraft.trim().length > 0;

  const commit = () => {
    setCustomSource(srcDraft);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div class="ds">
      <header class="header">
        <div class="header__inner">
          <a class="brand" href="/">
            <div class="brand__logo">N</div>
            <div class="brand__name">
              Nitter
              <small class="subtitle">Data source</small>
            </div>
          </a>
          <div class="header__actions">
            <a class="btn btn--sm" href="/">
              ← Dashboard
            </a>
          </div>
        </div>
      </header>

      {/* "Keep Nitter alive!" funding pledge — sticky under the header. */}
      <DonationBanner />

      <main class="container ds__main">
        <h1>Where instance data comes from</h1>
        <section class="ds-section">
          <p>The tracker does not discover Nitter instances by itself; it reads an uptime-JSON feed, a machine-readable snapshot of which Nitter instances are up, how fast they respond, and which version they run, and layers its own scoring on top. The Nitter data sources feed URL can overridden below.</p>
          {/*<p>Each <em>poll</em> (cron on the server, or the “Poll now” button) fetches the feed once, converts every entry into a 0–100 observation, blends it into a per-instance EWMA score, probes healthy instances for iframe-blocking headers, and writes the result to Cloudflare KV. The dashboard and the instance picker both read from that store.</p>*/}
          {/*<div class="ds-flow"><span class="ds-flow__step">uptime JSON feed</span><span class="ds-flow__arrow">→</span><span class="ds-flow__step">POST /api/poll</span><span class="ds-flow__arrow">→</span><span class="ds-flow__step">EWMA + bias scoring</span><span class="ds-flow__arrow">→</span><span class="ds-flow__step">KV store</span><span class="ds-flow__arrow">→</span><span class="ds-flow__step">GET /api/hosts · /v resolver</span></div>*/}
        </section>
        <section class="ds-section">
          <h2>Active source</h2>
          <div class="ds-source">
            <div class="ds-source__row">
              <span class="ds-source__label">Server default</span>
              <code>{info?.url ?? (err ? `unavailable (${err})` : "loading…")}</code>
            </div>
            <div class="ds-source__row">
              <span class="ds-source__label">This browser uses</span>
              <code class={usingCustom ? "ds-source__custom" : ""}>{effectiveUrl}</code>
            </div>
            {info && (
              <div class="ds-source__row">
                <span class="ds-source__label">Instances in last poll</span>
                <span>{info.host_count}</span>
              </div>
            )}
          </div>

          <label class="ds-custom">
            <span>Custom feed URL (stored in this browser)</span>
            <div class="set__input-row">
              <input
                type="url"
                placeholder="https://example.org/nitter-uptime.json"
                value={srcDraft}
                onInput={(e) => setSrcDraft((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => e.key === "Enter" && commit()}
                spellcheck={false}
              />
              <button class="btn btn--sm btn--primary" onClick={commit}>
                {saved ? "Saved ✓" : "Save"}
              </button>
              {usingCustom && (
                <button
                  class="btn btn--sm"
                  onClick={() => {
                    setCustomSource("");
                    setSrcDraft("");
                  }}
                >
                  Use default
                </button>
              )}
            </div>
            <small>
Used for “Poll now” as the feed source.
Must return JSON matching the schema below.
            </small>
          </label>
        </section>

        <section class="ds-section">
          <h2>Feed JSON schema</h2>
          <p>
            The poller expects a JSON object with a <code>hosts</code> array. Every field
            except <code>domain</code>, <code>url</code> and <code>healthy</code> is
            optional — missing fields simply contribute less to the score. Unknown
            fields are ignored, so extra metadata in your feed is harmless.
          </p>
          <FieldTable title="Top level" fields={TOP_FIELDS} />
          <FieldTable title="Each item in hosts[]" fields={HOST_FIELDS} />

          <h3 class="ds-derived-h">What the tracker derives from it</h3>
          <p>
            After each poll the tracker stores one entry per instance in KV — this is the
            object shape you get from <code>GET /api/hosts</code> and{" "}
            <code>GET /api/host/&lt;domain&gt;</code>:
          </p>
          <FieldTable title="Derived entry (KV)" fields={DERIVED_FIELDS} />
        </section>

        <section class="ds-section">
          <h2>Minimal working example</h2>
          <pre class="ds-pre">{`{
  "last_update": "2026-10-13T20:09:52Z",
  "hosts": [
    {
      "domain": "nitter.example.com",
      "url": "https://nitter.example.com",
      "healthy": true,
      "ping_avg": 120,
      "rss": true,
      "version_state": "Current",
      "last_healthy": "2026-10-13T20:09:40Z",
      "healthy_percentage_overall": 99
    }
  ]
}`}</pre>
          <p class="ds-note">
            Point <code>UPTIME_URL</code> (server) or the custom URL above (this browser)
            at any endpoint returning this shape and press <b>Poll now</b>. A 304/HTML
            response or a missing <code>hosts</code> array aborts the poll with an error.
          </p>
        </section>
      </main>
    </div>
  );
}
